<script setup lang="ts">
import { computed, ref } from 'vue';
import { ChevronRight } from 'lucide-vue-next';
import SyncPlayerLine from '@/components/Sync/SyncPlayerLine.vue';
import { useNowTicker } from '@/composables/useNowTicker';
import { ZONE_PLAYER_PREVIEW_LIMIT, useZonePlayersPreview } from '@/composables/api/useSchedulerSyncApi';
import {
    deriveZoneSyncState,
    describeChange,
    formatSyncTime,
    type SyncZone,
    type ZoneDeliveryStatus,
} from '@/utils/syncStatus';

/**
 * One zone in the Sync popover, on one line:
 * [chevron] [zone name] [player count] [state] [time of the latest change].
 *
 * The chevron opens a block under the row with one line per change and one
 * line per player. The players are fetched only when the block is opened.
 */
const props = defineProps<{
    zone: SyncZone;
    status: ZoneDeliveryStatus | undefined;
    statusLoading: boolean;
    /** The ids to show in place of the player count, for a zone with one or two players. */
    rowPlayerIds?: number[] | null;
}>();

const emit = defineEmits<{
    openZone: [zoneId: number];
    openPlayer: [locationZonePlayerId: number];
    showZonePlayers: [zoneIds: number[]];
}>();

const nowMs = useNowTicker();
const isExpanded = ref(false);

const zoneState = computed(() => (props.status ? deriveZoneSyncState(props.status) : null));

const stateLabel = computed(() => {
    if (zoneState.value) return zoneState.value.label;
    return props.statusLoading ? '…' : '—';
});

const playerCountLabel = computed(() => {
    if (!props.status) return '';
    return props.status.players === 1 ? '1 player' : `${props.status.players} players`;
});

const deliveryZoneIds = computed(() => props.status?.deliveryZoneIds ?? []);
const playersQuery = useZonePlayersPreview(deliveryZoneIds, isExpanded);
const players = computed(() => playersQuery.data.value?.players ?? []);
const totalPlayers = computed(() => playersQuery.data.value?.total ?? props.status?.players ?? 0);

const detailsId = computed(() => `sync-zone-details-${props.zone.zoneId}`);
</script>

<template>
    <!-- display: contents: these cells are placed straight into the popover's one grid
         (chevron / zone / ids / state), so every row shares its column widths. -->
    <div class="contents" data-testid="sync-zone-row">
        <div class="flex h-8 items-center">
            <button
                type="button"
                class="text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:ring-ring flex size-5 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-1"
                :aria-expanded="isExpanded"
                :aria-controls="detailsId"
                :aria-label="isExpanded ? 'Hide details' : 'Show details'"
                data-testid="sync-zone-toggle"
                @click="isExpanded = !isExpanded"
            >
                <ChevronRight
                    class="size-3.5 transition-transform duration-150"
                    :class="isExpanded ? 'rotate-90' : ''"
                    aria-hidden="true"
                />
            </button>
        </div>
        <div class="flex h-8 min-w-0 items-center text-sm">
            <button
                type="button"
                class="text-foreground hover:text-primary truncate text-left hover:underline focus-visible:outline-none focus-visible:underline"
                :title="zone.zoneName ?? `Zone #${zone.zoneId}`"
                data-testid="sync-zone-name"
                @click="emit('openZone', zone.zoneId)"
            >
                {{ zone.zoneName ?? `Zone #${zone.zoneId}` }}
            </button>
        </div>
        <div class="flex h-8 items-center gap-1.5 text-xs whitespace-nowrap tabular-nums" data-testid="sync-row-players">
            <template v-if="rowPlayerIds && rowPlayerIds.length > 0">
                <button
                    v-for="playerId in rowPlayerIds"
                    :key="playerId"
                    type="button"
                    class="text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:underline"
                    data-testid="sync-row-player"
                    @click="emit('openPlayer', playerId)"
                >
                    {{ playerId }}
                </button>
            </template>
            <span v-else class="text-muted-foreground">{{ playerCountLabel }}</span>
        </div>
        <!-- pr-2.5 here and on the player lines' labels: both sit the same distance in from
             the column's right edge, so they stay aligned, and the expanded block's background
             reaches past the player labels instead of cutting them off. -->
        <div class="flex h-8 items-center justify-end pr-2.5 text-xs font-semibold whitespace-nowrap" data-testid="sync-zone-state">
            {{ stateLabel }}
        </div>

        <!-- Spans every column and is a subgrid of them, so the player lines inside put
             their ids and states in the same columns as the rows. Padding on a subgrid
             would shift its items off the parent's columns, so the text cells carry their
             own right padding instead, and the player labels stay flush with the row states. -->
        <div
            v-if="isExpanded"
            :id="detailsId"
            class="bg-muted/40 col-span-full mb-1 grid grid-cols-subgrid gap-y-1.5 rounded py-2"
            data-testid="sync-zone-details"
        >
            <p v-if="zone.changes.length === 0" class="text-muted-foreground col-span-3 col-start-2 pr-2.5 text-xs">
                Last changed {{ formatSyncTime(zone.latestChangeAt, nowMs) }}
            </p>
            <div v-else class="col-span-3 col-start-2 flex flex-col gap-0.5 pr-2.5">
                <p v-for="change in zone.changes" :key="change.id" class="text-xs">
                    {{ describeChange(change) }}
                    <span class="text-muted-foreground">· {{ formatSyncTime(change.created_at, nowMs) }}</span>
                </p>
            </div>
            <p v-if="playersQuery.isLoading.value" class="text-muted-foreground col-span-3 col-start-2 pr-2.5 text-xs">
                Loading players…
            </p>
            <p v-else-if="playersQuery.isError.value" class="text-destructive-foreground col-span-3 col-start-2 pr-2.5 text-xs">
                Couldn't load the players.
            </p>
            <p
                v-else-if="deliveryZoneIds.length === 0 || players.length === 0"
                class="text-muted-foreground col-span-3 col-start-2 pr-2.5 text-xs"
            >
                No players to show.
            </p>
            <template v-else>
                <SyncPlayerLine
                    v-for="player in players"
                    :key="String(player.id)"
                    :player="player"
                    :content-changed-at="status?.contentChangedAt ?? null"
                    @open-player="emit('openPlayer', $event)"
                />
            </template>
            <button
                v-if="totalPlayers > ZONE_PLAYER_PREVIEW_LIMIT"
                type="button"
                class="text-muted-foreground hover:text-foreground col-start-2 justify-self-start text-xs hover:underline focus-visible:outline-none focus-visible:underline"
                @click="emit('showZonePlayers', deliveryZoneIds)"
            >
                Show all {{ totalPlayers }} players in search
            </button>
        </div>
    </div>
</template>
